import axios from 'axios';
import React, { useState, useEffect, useMemo } from 'react';
import { SENSORS_URL } from '../Constants';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import './SegmentSwitch.css';

const SegmentSwitch = ({
  activeOptionColor,
  activeBackgroundColor,
  activeTextColor,
  inactiveTextColor,
  inactiveBackgroundColor,
  fontSize,
  title,
  transparentBackground,
  options,
  value,
  draggable,
  pin,
  did,
  tid,
}) => {
  const [selectedValue, setSelectedValue] = useState(value);
  const [cursorState, setCursorState] = useState('idle');

  useEffect(() => {
    const selectedItem = options?.find((x) => String(x.value) === String(value));
    setSelectedValue(selectedItem?.value ?? value);
  }, [value, options]);

  const rootStyle = useMemo(() => {
    const style = {};
    const resolvedActiveBackground = activeBackgroundColor || activeOptionColor;
    const resolvedFontSize = Number(fontSize);

    if (resolvedActiveBackground) {
      style['--segment-accent'] = resolvedActiveBackground;
      style['--segment-bg-selected'] = resolvedActiveBackground;
      style['--segment-selected-bg-mixed'] = resolvedActiveBackground;
      style['--segment-bg-selected-hover'] = resolvedActiveBackground;
      style['--segment-selected-hover-bg-mixed'] = resolvedActiveBackground;
    }
    if (inactiveBackgroundColor) {
      style['--segment-group-bg'] = inactiveBackgroundColor;
    }
    if (activeTextColor) {
      style['--segment-selected-text'] = activeTextColor;
    }
    if (inactiveTextColor) {
      style['--segment-unselected-text'] = inactiveTextColor;
    }
    if (Number.isFinite(resolvedFontSize) && resolvedFontSize > 0) {
      style['--segment-font-size'] = `${resolvedFontSize}px`;
      style['--segment-button-min-height'] = `${Math.max(39, resolvedFontSize * 2.4)}px`;
      style['--segment-button-radius'] = `${Math.max(8, resolvedFontSize * 0.5)}px`;
    }

    return Object.keys(style).length > 0 ? style : undefined;
  }, [
    activeBackgroundColor,
    activeOptionColor,
    inactiveBackgroundColor,
    activeTextColor,
    inactiveTextColor,
    fontSize,
  ]);

  const handleChange = (event, newValue) => {
    if (newValue !== null) {
      setSelectedValue(newValue);

      if (did) {
        const body = JSON.stringify({
          device: did,
          template: tid,
          data: { [pin]: newValue.toString() },
        });

        axios
          .post(`${SENSORS_URL}/control`, body, {
            headers: { 'Content-Type': 'application/json' },
          })
          .then((res) => console.log(res.data))
          .catch(() => setSelectedValue(value));
      }
    }
  };

  const cursorClass = draggable
    ? cursorState === 'grabbing'
      ? 'segment-switch--grabbing'
      : 'segment-switch--grab'
    : 'segment-switch--auto';

  return (
    <div
      className={[
        'widget-container',
        'segment-switch',
        cursorClass,
        transparentBackground ? 'segment-switch--transparent' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={rootStyle}
      onMouseDown={() => draggable && setCursorState('grabbing')}
      onMouseUp={() => draggable && setCursorState('grab')}
      onMouseLeave={() => draggable && setCursorState('grab')}
      draggable={draggable}
      onDragStart={(e) => {
        const id = e.target.closest('.widget')?.id;
        if (!id) return;

        const rect = e.target.getBoundingClientRect();
        const offsetX = e.clientX - rect.x;
        const offsetY = e.clientY - rect.y;

        const dragData = `segment-switch;${id};${offsetX};${offsetY};${id.startsWith('base') ? 'add' : 'move'}`;
        e.dataTransfer.setData('text/plain', dragData);
      }}
    >
      {title ? <h3 className="segment-switch__title">{title}</h3> : null}

      <div className="segment-switch__wrap">
        <ToggleButtonGroup
          value={selectedValue}
          exclusive
          onChange={handleChange}
          color="primary"
          className="segment-switch__group"
        >
          {options?.map((option) => (
            <ToggleButton key={option.value} value={option.value} size="small" className="segment-switch__btn">
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>
    </div>
  );
};

export default SegmentSwitch;
